/**
 * 정오 표기 컨트롤(페이지 로컬, 표시 전용 — 결정 A1·G1).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * props로 받은 줄 상태(mode·draft·override 파생 held)를 그린다 — 전이·네트워크·캐시는 페이지 몫이다.
 * verdict 3버튼·(개입 필요) 등급 select·메모·[저장]·[취소]·[표기 취소]·보기 표기·"저장된 값" 병기·
 * 성분 중복 제외 문구·같은 입력 N줄·룰 메모 안내·notLabelable 비활성 + 사유. "처방 경고" 블록엔 없다.
 */
import React from "react";
import Button from "components/atoms/Button";
import {
  VERDICT_LABELS,
  VERDICT_SEVERITY_OPTIONS,
  SCOPE_NOTE_TEXT,
  canSave,
  labelViewMain,
  savedValueText,
  sameCaseText,
  sameCaseTooltip,
  ruleNoteNoticeText,
  notLabelableText,
  type LineState,
} from "utils/doseReviewLabels";
import { apiRangeToPeriod } from "utils/doseReviewLogs";
import type {
  DoseLabel,
  DoseLabelSeverity,
  DoseLabelVerdict,
  DoseReviewLogDetailItem,
} from "services/api/doseReviewLogs/types";
import {
  LabelWrap,
  VerdictRow,
  LabelSelect,
  LabelTextarea,
  ScopeNote,
  RuleNoteNotice,
  SameCaseNote,
  SavedValue,
  ViewLine,
  ViewMemo,
  NotLabelable,
  Actions,
} from "./labelStyle";

export interface LabelControlProps {
  item: DoseReviewLogDetailItem;
  held: DoseLabel | null;
  line: LineState;
  sameCaseRange: { from: string; to: string };
  onVerdict: (verdict: DoseLabelVerdict) => void;
  onSeverity: (value: DoseLabelSeverity | null) => void;
  onNote: (value: string) => void;
  onSave: () => void;
  onDraftCancel: () => void;
  onUnlabel: () => void;
}

const VERDICTS: DoseLabelVerdict[] = ["intervene", "no_intervene", "undecidable"];

const LabelControl: React.FC<LabelControlProps> = ({
  item,
  held,
  line,
  sameCaseRange,
  onVerdict,
  onSeverity,
  onNote,
  onSave,
  onDraftCancel,
  onUnlabel,
}) => {
  const { mode, draft } = line;
  const busy = mode === "saving" || mode === "cancelling";

  if (item.notLabelableReason) {
    return (
      <LabelWrap data-testid="dose-label-control">
        <VerdictRow>
          {VERDICTS.map((v) => (
            <Button
              key={v}
              data-testid={`dose-label-verdict-${v}`}
              buttonType="button"
              type="Text"
              size="Small"
              variant="Outlined"
              color="Secondary"
              disabled
            >
              {VERDICT_LABELS[v]}
            </Button>
          ))}
        </VerdictRow>
        <NotLabelable data-testid="dose-label-not-labelable">
          {notLabelableText(item.notLabelableReason)}
        </NotLabelable>
      </LabelWrap>
    );
  }

  const inDraft = draft != null; // 작성 중·저장 중(초안 보존)
  const period = apiRangeToPeriod(sameCaseRange);
  const lineCount = item.sameCaseLineCount ?? 0;

  return (
    <LabelWrap data-testid="dose-label-control">
      <VerdictRow>
        {VERDICTS.map((v) => (
          <Button
            key={v}
            data-testid={`dose-label-verdict-${v}`}
            buttonType="button"
            type="Text"
            size="Small"
            variant={draft?.verdict === v ? "Contained" : "Outlined"}
            color={draft?.verdict === v ? "Primary" : "Secondary"}
            disabled={busy}
            onClick={() => onVerdict(v)}
          >
            {VERDICT_LABELS[v]}
          </Button>
        ))}
      </VerdictRow>

      {inDraft && draft.verdict === "intervene" && (
        <LabelSelect
          data-testid="dose-label-severity"
          value={draft.verdictSeverity ?? ""}
          disabled={busy}
          onChange={(e) => onSeverity((e.target.value || null) as DoseLabelSeverity | null)}
        >
          <option value="">등급 선택</option>
          {VERDICT_SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </LabelSelect>
      )}

      {inDraft && (
        <LabelTextarea
          data-testid="dose-label-note-input"
          maxLength={500}
          value={draft.verdictNote}
          disabled={busy}
          placeholder="메모(선택 — 판단 불가는 필수)"
          onChange={(e) => onNote(e.target.value)}
        />
      )}

      <ScopeNote data-testid="dose-label-scope-note">{SCOPE_NOTE_TEXT}</ScopeNote>

      {item.noteTarget && draft?.verdict === "no_intervene" && (
        <RuleNoteNotice data-testid="dose-label-rule-note-notice">
          {ruleNoteNoticeText(item.noteTarget.ruleId)}
        </RuleNoteNotice>
      )}

      {lineCount >= 2 && (
        <SameCaseNote
          data-testid="dose-label-same-case"
          title={sameCaseTooltip(period.start, period.end)}
        >
          {sameCaseText(lineCount, item.sameCasePrescriptionCount ?? 0)}
        </SameCaseNote>
      )}

      {inDraft && (
        <SavedValue data-testid="dose-label-saved-value">{savedValueText(held)}</SavedValue>
      )}

      {mode === "view" && held && (
        <ViewLine data-testid="dose-label-view">
          <div>{labelViewMain(held)}</div>
          {held.verdictNote ? <ViewMemo>{held.verdictNote}</ViewMemo> : null}
        </ViewLine>
      )}

      <Actions>
        {inDraft && (
          <Button
            data-testid="dose-label-save"
            buttonType="button"
            type="Text"
            size="Small"
            variant="Contained"
            color="Primary"
            disabled={busy || !canSave(draft)}
            onClick={onSave}
          >
            저장
          </Button>
        )}
        {mode === "editing" && (
          <Button
            data-testid="dose-label-draft-cancel"
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            onClick={onDraftCancel}
          >
            취소
          </Button>
        )}
        {held && (
          <Button
            data-testid="dose-label-unlabel"
            buttonType="button"
            type="Text"
            size="Small"
            variant="Ghost"
            color="Negative"
            disabled={mode !== "view"}
            onClick={onUnlabel}
          >
            표기 취소
          </Button>
        )}
      </Actions>
    </LabelWrap>
  );
};

export default LabelControl;
